import React from 'react';
import PmsWebhookPanel from './PmsWebhookPanel';
import { formatDateTime } from '../../utils/dateFormat';

// Providers whose events can be pushed to POST /api/pms/webhooks/:provider,
// and the field holding the property code the webhook must send — mirrors
// backend services/pmsWebhookConfig.service.js PROPERTY_FIELD.
const WEBHOOK_PROPERTY_FIELD = {
    capitaland: 'propertyCode',
    wincloud:   'propNm',
    winhms:     'hotelCode',
    idsnext:    'hotelCode',
    hotla:      'propNm',
    fias:       'propertyCode',
};

/**
 * ntfy login notifications + manual-approval gate + per-room device limit —
 * identical behavior for every PMS provider (see portal.controller.js's
 * getActivePmsConfig, which reads these 3 fields generically off whichever
 * provider is active), so this is rendered once per active provider instead
 * of being copy-pasted into each provider's block. It used to live only
 * inside the CapitaLand block, which is why it silently disappeared for
 * every other provider — factored out here so that can't happen again.
 */
const PmsNotificationFields = ({ provider, providerLabel, formData, handleInputChange }) => {
    const cfg = formData.integrations[provider];
    const path = (field) => `integrations.${provider}.${field}`;

    return (
        <>
            <div className="form-group-modern full-width">
                <label>Devices Per Room</label>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 400 }}>
                        <input
                            type="radio"
                            name={`${provider}DeviceLimit`}
                            checked={!cfg.maxDevicesPerRoom}
                            onChange={() => handleInputChange({ target: { name: path('maxDevicesPerRoom'), value: null, type: 'text' } })}
                        />
                        Unlimited
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 400 }}>
                        <input
                            type="radio"
                            name={`${provider}DeviceLimit`}
                            checked={!!cfg.maxDevicesPerRoom}
                            onChange={() => handleInputChange({ target: { name: path('maxDevicesPerRoom'), value: 2, type: 'text' } })}
                        />
                        Limited
                    </label>
                </div>
                {!!cfg.maxDevicesPerRoom && (
                    <input
                        type="number"
                        name={path('maxDevicesPerRoom')}
                        min="1"
                        max="20"
                        value={cfg.maxDevicesPerRoom}
                        onChange={handleInputChange}
                        style={{ maxWidth: '8rem' }}
                    />
                )}
                <p className="input-hint">
                    {cfg.maxDevicesPerRoom
                        ? `Max ${cfg.maxDevicesPerRoom} device(s) allowed per room at the same time.`
                        : 'All devices sharing the same room + last name will be allowed (no cap).'}
                </p>
            </div>

            <div className="form-group-modern full-width">
                <label>
                    ntfy Login Notifications
                    <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: '0.4rem' }}>(optional)</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>ntfy.hotwifi.in /</span>
                    <input
                        type="text"
                        name={path('ntfyTopic')}
                        value={cfg.ntfyTopic || ''}
                        onChange={handleInputChange}
                        placeholder={`${providerLabel.replace(/\s+/g, '')}Branch`}
                        autoComplete="off"
                        style={{ flex: 1 }}
                    />
                </div>
                <p className="input-hint">
                    Each room login attempt will send a push notification to this ntfy topic. Leave blank to disable.
                </p>
            </div>

            <div className="form-group-modern full-width">
                <div className="integ-toggle-row" style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                    <div>
                        <p className="integ-toggle-label">
                            Manual Approval Gate
                            {cfg.manualApproval && (
                                <span style={{ marginLeft: 8, fontSize: '0.72rem', background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                    ENABLED
                                </span>
                            )}
                        </p>
                        <p className="integ-toggle-sub">
                            When enabled, every PMS login pauses for admin approval via ntfy before granting internet access. Requires ntfy topic to be set.
                        </p>
                    </div>
                    <label className="switch-modern">
                        <input
                            type="checkbox"
                            name={path('manualApproval')}
                            checked={!!cfg.manualApproval}
                            onChange={handleInputChange}
                        />
                        <span className="slider-modern round"></span>
                    </label>
                </div>
            </div>
        </>
    );
};

/**
 * Shared PMS provider picker + per-provider credential fields — used by both
 * Branches.jsx's "Edit Branch > Integrations" tab and Integrations.jsx's
 * "Configure PMS" modal. Extracted so the two stop drifting out of sync:
 * before this, Integrations.jsx only ever supported CapitaLand while this
 * exact field set (all 5 real providers) already existed here, unused by it.
 *
 * `formData` must have the shape:
 *   { integrations: { pms: {provider, enabled, apiUrl, apiKey},
 *                      capitaland: {...}, wincloud: {...}, winhms: {...},
 *                      idsnext: {...}, hotla: {...}, hotelkey: {...}, innkey: {...} } }
 * HotelKey / InnKey keys are write-only: they arrive blank with
 * `<key>Set: true`, and a blank field keeps the saved key.
 * `handleInputChange` must be a generic dot-path updater — see
 * Branches.jsx's own handleInputChange for the reference implementation
 * (splits `name="integrations.wincloud.serverUrl"` and sets it on nested
 * state).
 *
 * Opera PMS and Protel were removed from the provider list — neither ever
 * had a backend integration (no opera.service.js / protel.service.js, no
 * dispatch in pms.controller.js), so selecting either silently did nothing.
 *
 * `branchId` (the saved branch being edited, if any) enables the per-branch
 * webhook panel; without it the panel asks for the branch to be saved first.
 */
const PmsProviderFields = ({
    formData,
    handleInputChange,
    modalBranchPages = [],
    admin,
    loadingDefaults = false,
    onLoadDefaults,
    branchId,
}) => {
    const provider = formData.integrations.pms.provider;
    const webhookField = WEBHOOK_PROPERTY_FIELD[provider];

    return (
        <>
            {onLoadDefaults && admin?.role === 'superadmin' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                    <button
                        type="button"
                        onClick={onLoadDefaults}
                        disabled={loadingDefaults}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.875rem', borderRadius: '6px', border: '1.5px solid #7c3aed', background: 'transparent', color: '#7c3aed', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                        <i className={`fas ${loadingDefaults ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                        {loadingDefaults ? 'Loading...' : 'Load System Defaults (CapitaLand)'}
                    </button>
                </div>
            )}

            <div className="pms-config">
                <div className="form-grid">
                    <div className="form-group-modern">
                        <label>PMS Provider</label>
                        <select
                            name="integrations.pms.provider"
                            value={provider}
                            onChange={handleInputChange}
                        >
                            <option value="idsnext">IDS Next (IDS Cosmos)</option>
                            <option value="winhms">WinHMS</option>
                            <option value="wincloud">WinCloud PMS</option>
                            <option value="capitaland">CapitaLand ODX</option>
                            <option value="hotla">HOTLA</option>
                            <option value="hotelkey">HotelKey</option>
                            <option value="innkey">InnKey</option>
                            <option value="fias">FIAS (Fidelio Interface)</option>
                        </select>
                    </div>
                    <div className="form-group-modern">
                        <label>Enable PMS Sync</label>
                        <div style={{ padding: '10px 0' }}>
                            <label className="switch-modern">
                                <input
                                    type="checkbox"
                                    name="integrations.pms.enabled"
                                    checked={formData.integrations.pms.enabled}
                                    onChange={handleInputChange}
                                />
                                <span className="slider-modern round"></span>
                            </label>
                        </div>
                    </div>

                    {/* WinCloud PMS credentials */}
                    {provider === 'wincloud' && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#15803d', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-cloud"></i>
                                WinCloud PMS WiFi API Credentials
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#16a34a', margin: 0 }}>
                                Credentials provided by WinCloud. Guests sync automatically every configured interval.
                            </p>
                        </div>
                        <div className="form-group-modern full-width">
                            <label>Server URL</label>
                            <input
                                type="text"
                                name="integrations.wincloud.serverUrl"
                                value={formData.integrations.wincloud.serverUrl}
                                onChange={handleInputChange}
                                placeholder="http://inddns.wincloudpms.net:8009"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Property Name (PropNm)</label>
                            <input
                                type="text"
                                name="integrations.wincloud.propNm"
                                value={formData.integrations.wincloud.propNm}
                                onChange={handleInputChange}
                                placeholder="e.g. ROSEHOTELS"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Company ID (CID)</label>
                            <input
                                type="text"
                                name="integrations.wincloud.cid"
                                value={formData.integrations.wincloud.cid}
                                onChange={handleInputChange}
                                placeholder="e.g. W1"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>API Key (X-APIKey)</label>
                            <input
                                type="password"
                                name="integrations.wincloud.apiKey"
                                value={formData.integrations.wincloud.apiKey}
                                onChange={handleInputChange}
                                placeholder="••••••••••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Sync Interval (minutes)</label>
                            <input
                                type="number"
                                name="integrations.wincloud.syncInterval"
                                value={formData.integrations.wincloud.syncInterval}
                                onChange={handleInputChange}
                                min="1"
                                max="60"
                                placeholder="5"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.wincloud.routesTo"
                                value={formData.integrations.wincloud.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where successful WinCloud check-ins write their occupancy record.</p>
                        </div>
                        <PmsNotificationFields provider="wincloud" providerLabel="WinCloud" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* WinHMS credentials */}
                    {provider === 'winhms' && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#fefce8', border: '1px solid #fef08a', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#854d0e', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-hotel"></i>
                                WinHMS PMS API Credentials
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#a16207', margin: 0 }}>
                                Credentials from your WinHMS server. Guests sync automatically every configured interval.
                            </p>
                        </div>
                        <div className="form-group-modern full-width">
                            <label>Server URL</label>
                            <input
                                type="text"
                                name="integrations.winhms.serverUrl"
                                value={formData.integrations.winhms.serverUrl}
                                onChange={handleInputChange}
                                placeholder="http://your-hotel.com:8080"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Hotel Code</label>
                            <input
                                type="text"
                                name="integrations.winhms.hotelCode"
                                value={formData.integrations.winhms.hotelCode}
                                onChange={handleInputChange}
                                placeholder="e.g. HOTEL01"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Username</label>
                            <input
                                type="text"
                                name="integrations.winhms.username"
                                value={formData.integrations.winhms.username}
                                onChange={handleInputChange}
                                placeholder="API username"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Password</label>
                            <input
                                type="password"
                                name="integrations.winhms.password"
                                value={formData.integrations.winhms.password}
                                onChange={handleInputChange}
                                placeholder="••••••••••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Sync Interval (minutes)</label>
                            <input
                                type="number"
                                name="integrations.winhms.syncInterval"
                                value={formData.integrations.winhms.syncInterval}
                                onChange={handleInputChange}
                                min="1"
                                max="60"
                                placeholder="5"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.winhms.routesTo"
                                value={formData.integrations.winhms.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where successful WinHMS check-ins write their occupancy record.</p>
                        </div>
                        <PmsNotificationFields provider="winhms" providerLabel="WinHMS" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* IDS Next credentials */}
                    {provider === 'idsnext' && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#7e22ce', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-building"></i>
                                IDS Next (IDS Cosmos) PMS Credentials
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#9333ea', margin: 0 }}>
                                Credentials from your IDS Next / IDS Cosmos server. Guests sync automatically every configured interval.
                            </p>
                        </div>
                        <div className="form-group-modern full-width">
                            <label>Server URL</label>
                            <input
                                type="text"
                                name="integrations.idsnext.serverUrl"
                                value={formData.integrations.idsnext.serverUrl}
                                onChange={handleInputChange}
                                placeholder="https://your-hotel.idsnext.com"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Hotel Code</label>
                            <input
                                type="text"
                                name="integrations.idsnext.hotelCode"
                                value={formData.integrations.idsnext.hotelCode}
                                onChange={handleInputChange}
                                placeholder="e.g. IDS001"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Username</label>
                            <input
                                type="text"
                                name="integrations.idsnext.username"
                                value={formData.integrations.idsnext.username}
                                onChange={handleInputChange}
                                placeholder="API username"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Password</label>
                            <input
                                type="password"
                                name="integrations.idsnext.password"
                                value={formData.integrations.idsnext.password}
                                onChange={handleInputChange}
                                placeholder="••••••••••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Sync Interval (minutes)</label>
                            <input
                                type="number"
                                name="integrations.idsnext.syncInterval"
                                value={formData.integrations.idsnext.syncInterval}
                                onChange={handleInputChange}
                                min="1"
                                max="60"
                                placeholder="5"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.idsnext.routesTo"
                                value={formData.integrations.idsnext.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where successful IDS Next check-ins write their occupancy record.</p>
                        </div>
                        <PmsNotificationFields provider="idsnext" providerLabel="IDSNext" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* HOTLA Wi-Fi Room Authentication API */}
                    {provider === 'hotla' && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0369a1', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-wifi"></i>
                                HOTLA Wi-Fi Room Authentication API
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#0284c7', margin: 0 }}>
                                Verified live at each guest login (Room Number + Last Name), and also syncs periodically below so Room Management shows current occupancy before any guest has logged in.
                            </p>
                        </div>
                        <div className="form-group-modern">
                            <label>Property Code (PropNm)</label>
                            <input
                                type="text"
                                name="integrations.hotla.propNm"
                                value={formData.integrations.hotla.propNm}
                                onChange={handleInputChange}
                                placeholder="e.g. CL0002"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>API Key</label>
                            <input
                                type="password"
                                name="integrations.hotla.apiKey"
                                value={formData.integrations.hotla.apiKey}
                                onChange={handleInputChange}
                                placeholder="••••••••••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>API URL</label>
                            <input
                                type="text"
                                name="integrations.hotla.serverUrl"
                                value={formData.integrations.hotla.serverUrl}
                                onChange={handleInputChange}
                                placeholder="https://hotla.com/hotlaapi/wifiapi.php"
                                style={{ fontFamily: 'monospace' }}
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Sync Interval (minutes)</label>
                            <input
                                type="number"
                                name="integrations.hotla.syncInterval"
                                value={formData.integrations.hotla.syncInterval}
                                onChange={handleInputChange}
                                min="1"
                                max="60"
                                placeholder="5"
                            />
                            <p className="input-hint">How often to pull the full guest list. Live per-login verification happens separately and is unaffected by this.</p>
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.hotla.routesTo"
                                value={formData.integrations.hotla.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where successful HOTLA check-ins write their occupancy record.</p>
                        </div>
                        <PmsNotificationFields provider="hotla" providerLabel="HOTLA" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* HotelKey Datastream (poll model) */}
                    {provider === 'hotelkey' && formData.integrations.hotelkey && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9a3412', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-key"></i>
                                HotelKey Datastream (Poll)
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#c2410c', margin: 0 }}>
                                Check-ins, room moves and check-outs are read from HotelKey's event queue every minute and kept in Room Management;
                                guests sign in with Room Number + Last Name against those rooms. The first read (and Sync Now) asks HotelKey to send today's in-house guests.
                            </p>
                        </div>
                        <div className="form-group-modern full-width">
                            <label>Host</label>
                            <input
                                type="text"
                                name="integrations.hotelkey.serverUrl"
                                value={formData.integrations.hotelkey.serverUrl}
                                onChange={handleInputChange}
                                placeholder="https://00.us-east-1.hotelkeyapp.com"
                                style={{ fontFamily: 'monospace' }}
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>App ID (username)</label>
                            <input
                                type="text"
                                name="integrations.hotelkey.appId"
                                value={formData.integrations.hotelkey.appId}
                                onChange={handleInputChange}
                                placeholder="Queue id given by HotelKey"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>App Secret (password)</label>
                            <input
                                type="password"
                                name="integrations.hotelkey.appSecret"
                                value={formData.integrations.hotelkey.appSecret}
                                onChange={handleInputChange}
                                placeholder={formData.integrations.hotelkey.appSecretSet ? '•••••••• (saved — leave blank to keep)' : ''}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Property ID</label>
                            <input
                                type="text"
                                name="integrations.hotelkey.propertyId"
                                value={formData.integrations.hotelkey.propertyId}
                                onChange={handleInputChange}
                                placeholder="HotelKey property UUID"
                                autoComplete="off"
                            />
                            <p className="input-hint">Which hotel's events on the queue are this branch's. Needed when several branches share one App ID.</p>
                        </div>
                        <div className="form-group-modern">
                            <label>Property Code</label>
                            <input
                                type="text"
                                name="integrations.hotelkey.propertyCode"
                                value={formData.integrations.hotelkey.propertyCode}
                                onChange={handleInputChange}
                                placeholder="e.g. BWITI"
                                autoComplete="off"
                            />
                            <p className="input-hint">Lets a guest who just checked in sign in before their event arrives (live room lookup), and enables the connection test.</p>
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.hotelkey.routesTo"
                                value={formData.integrations.hotelkey.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where HotelKey check-ins write their occupancy record.</p>
                        </div>
                        <PmsNotificationFields provider="hotelkey" providerLabel="HotelKey" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* InnKey Wi-Fi Interface API */}
                    {provider === 'innkey' && formData.integrations.innkey && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#991b1b', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-door-open"></i>
                                InnKey Wi-Fi Interface API
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#b91c1c', margin: 0 }}>
                                The occupied-room list is pulled every sync interval into Room Management (every guest of a room can sign in with their own last name);
                                a room InnKey stops listing is checked out and its Wi-Fi ends.
                            </p>
                        </div>
                        <div className="form-group-modern full-width">
                            <label>Endpoint</label>
                            <input
                                type="text"
                                name="integrations.innkey.serverUrl"
                                value={formData.integrations.innkey.serverUrl}
                                onChange={handleInputChange}
                                placeholder="https://api-staging.innkeyapp.com"
                                style={{ fontFamily: 'monospace' }}
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Partner (x-partner)</label>
                            <input
                                type="text"
                                name="integrations.innkey.partner"
                                value={formData.integrations.innkey.partner}
                                onChange={handleInputChange}
                                placeholder="Given by InnKey"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Access Key (x-accesskey)</label>
                            <input
                                type="password"
                                name="integrations.innkey.accessKey"
                                value={formData.integrations.innkey.accessKey}
                                onChange={handleInputChange}
                                placeholder={formData.integrations.innkey.accessKeySet ? '•••••••• (saved — leave blank to keep)' : 'Partner-level key'}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Hotel Key (shared / security key)</label>
                            <input
                                type="password"
                                name="integrations.innkey.sharedKey"
                                value={formData.integrations.innkey.sharedKey}
                                onChange={handleInputChange}
                                placeholder={formData.integrations.innkey.sharedKeySet ? '•••••••• (saved — leave blank to keep)' : 'Per-hotel key'}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Sync Interval (minutes)</label>
                            <input
                                type="number"
                                name="integrations.innkey.syncInterval"
                                value={formData.integrations.innkey.syncInterval}
                                onChange={handleInputChange}
                                min="1"
                                max="60"
                                placeholder="5"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.innkey.routesTo"
                                value={formData.integrations.innkey.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where InnKey check-ins write their occupancy record.</p>
                        </div>
                        <PmsNotificationFields provider="innkey" providerLabel="InnKey" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* CapitaLand ODX credentials */}
                    {provider === 'capitaland' && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0369a1', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-wifi"></i>
                                CapitaLand ODX WiFi API Credentials
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#0284c7', margin: 0 }}>
                                Property-specific credentials provided by ODX. Used to verify guest WiFi access via the CapitaLand getstatus API.
                            </p>
                        </div>
                        <div className="form-group-modern">
                            <label>Client ID</label>
                            <input
                                type="text"
                                name="integrations.capitaland.clientId"
                                value={formData.integrations.capitaland.clientId}
                                onChange={handleInputChange}
                                placeholder="e.g. 2bea9f5f5de84c36..."
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Client Secret</label>
                            <input
                                type="password"
                                name="integrations.capitaland.clientSecret"
                                value={formData.integrations.capitaland.clientSecret}
                                onChange={handleInputChange}
                                placeholder="••••••••••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Terminal ID</label>
                            <input
                                type="text"
                                name="integrations.capitaland.terminalId"
                                value={formData.integrations.capitaland.terminalId}
                                onChange={handleInputChange}
                                placeholder="e.g. 4be61393-ee32-4502..."
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Property Code</label>
                            <input
                                type="text"
                                name="integrations.capitaland.propertyCode"
                                value={formData.integrations.capitaland.propertyCode}
                                onChange={handleInputChange}
                                placeholder="e.g. 41280"
                                autoComplete="off"
                            />
                        </div>
                        <div className="form-group-modern">
                            <label>Environment</label>
                            <select
                                name="integrations.capitaland.environment"
                                value={formData.integrations.capitaland.environment}
                                onChange={handleInputChange}
                            >
                                <option value="production">Production</option>
                                <option value="uat">UAT (Testing)</option>
                            </select>
                        </div>
                        <div className="form-group-modern">
                            <label>Room Auth Suffix</label>
                            <input
                                type="text"
                                name="integrations.capitaland.roomAuthSuffix"
                                value={formData.integrations.capitaland.roomAuthSuffix}
                                onChange={handleInputChange}
                                placeholder="ORNRP"
                                autoComplete="off"
                            />
                            <p className="input-hint">
                                Guests only enter Room Number. This is appended (space-separated) before sending to ODX, e.g. "12345" → "12345 {formData.integrations.capitaland.roomAuthSuffix || 'ORNRP'}".
                            </p>
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.capitaland.routesTo"
                                value={formData.integrations.capitaland.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">
                                Where successful CapitaLand check-ins write their occupancy record. "Rooms" is the existing behavior — pick a custom Branch Page instead to route this branch's guests there. {modalBranchPages.length === 0 && 'No custom pages configured yet for this branch (see Branch Pages).'}
                            </p>
                        </div>

                        <PmsNotificationFields provider="capitaland" providerLabel="Oakwood" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {/* FIAS (Fidelio Interface Application Specification) */}
                    {provider === 'fias' && (<>
                        <div className="form-group-modern full-width" style={{ gridColumn: '1 / -1', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 16px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e3a8a', fontWeight: 600, marginBottom: '4px' }}>
                                <i className="fas fa-plug"></i>
                                FIAS — Events Pushed to HotWiFi
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#1e40af', margin: 0 }}>
                                FIAS doesn't get polled — guest check-in, guest change, room move and check-out
                                events are pushed to us in real time, usually by the FIAS bridge running at the
                                hotel (see the webhook panel below for its URL, Property Code and secret). Each
                                branch needs its own unique Property Code so its events land on this branch.
                            </p>
                        </div>
                        <div className="form-group-modern">
                            <label>Property Code</label>
                            <input
                                type="text"
                                name="integrations.fias.propertyCode"
                                value={formData.integrations.fias.propertyCode}
                                onChange={handleInputChange}
                                placeholder="e.g. CL0002"
                                autoComplete="off"
                            />
                            <p className="input-hint">Unique per branch — the bridge's PROPERTY_CODE (or the code your PMS's FIAS interface sends) must match it exactly.</p>
                        </div>
                        <div className="form-group-modern">
                            <label>Guest Sync</label>
                            <div style={{ padding: '10px 0' }}>
                                <label className="switch-modern">
                                    <input type="checkbox" name="integrations.fias.guestSync" checked={formData.integrations.fias.guestSync !== false} onChange={handleInputChange} />
                                    <span className="slider-modern round"></span>
                                </label>
                            </div>
                        </div>
                        <div className="form-group-modern">
                            <label>Room Sync</label>
                            <div style={{ padding: '10px 0' }}>
                                <label className="switch-modern">
                                    <input type="checkbox" name="integrations.fias.roomSync" checked={formData.integrations.fias.roomSync !== false} onChange={handleInputChange} />
                                    <span className="slider-modern round"></span>
                                </label>
                            </div>
                        </div>
                        <div className="form-group-modern">
                            <label>Module Mapping — Routes To</label>
                            <select
                                name="integrations.fias.routesTo"
                                value={formData.integrations.fias.routesTo || 'rooms'}
                                onChange={handleInputChange}
                            >
                                <option value="rooms">Rooms (default)</option>
                                {modalBranchPages.map(p => (
                                    <option key={p._id} value={p.slug}>{p.name}</option>
                                ))}
                            </select>
                            <p className="input-hint">Where successful FIAS check-ins write their occupancy record.</p>
                        </div>
                        {formData.integrations.fias.connectionStatus && formData.integrations.fias.connectionStatus !== 'never' && (
                            <div className="form-group-modern full-width">
                                <label>Live Connection Status</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                                    <span style={{
                                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                        background: formData.integrations.fias.connectionStatus === 'connected' ? '#22c55e' : '#9ca3af',
                                    }}></span>
                                    {formData.integrations.fias.connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                                    {formData.integrations.fias.lastMessageAt && ` — last message: ${formatDateTime(formData.integrations.fias.lastMessageAt)}`}
                                </div>
                            </div>
                        )}
                        <PmsNotificationFields provider="fias" providerLabel="FIAS" formData={formData} handleInputChange={handleInputChange} />
                    </>)}

                    {webhookField && (
                        <PmsWebhookPanel
                            branchId={branchId}
                            provider={provider}
                            formPropertyCode={formData.integrations[provider]?.[webhookField]}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default PmsProviderFields;
